"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

const TargetCursor = dynamic(() => import("./TargetCursor"), {
  ssr: false,
  loading: () => null,
});

type DeferredTargetCursorProps = {
  targetSelector?: string;
  spinDuration?: number;
  hideDefaultCursor?: boolean;
};

/**
 * Mount the GSAP cursor only after idle (or first mouse move).
 * Avoids getBoundingClientRect / GSAP work during LCP and TBT window.
 */
export default function DeferredTargetCursor(props: DeferredTargetCursorProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const enable = () => {
      if (!cancelled) setReady(true);
    };

    const cleanupSchedule = () => {
      window.removeEventListener("pointermove", onFirstMove);
      if (idleId !== undefined && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };

    const onFirstMove = () => {
      enable();
      cleanupSchedule();
    };

    window.addEventListener("pointermove", onFirstMove, {
      once: true,
      passive: true,
    });

    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(enable, { timeout: 3500 });
    } else {
      timeoutId = setTimeout(enable, 2000);
    }

    return () => {
      cancelled = true;
      cleanupSchedule();
    };
  }, []);

  if (!ready) return null;
  return <TargetCursor {...props} />;
}
